import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Bell, Plus } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import SectionLabel from "@/components/site/SectionLabel";
import CardMark from "@/components/site/CardMark";
import Seo from "@/components/site/Seo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitAttendeeInterest } from "@/lib/api";
import { BRAND } from "@/lib/constants";

const EXPERIENCE = [
  {
    h: "Emerging Voices",
    p: "A roster of contestants bringing real messages — not rehearsed motivational slogans.",
  },
  {
    h: "Live Production",
    p: "Lights, sound, and a stage worth showing up for. Treated with care.",
  },
  {
    h: "Structured Competition",
    p: "Honest judging, transparent format, a moment that matters for every speaker.",
  },
  {
    h: "Community Room",
    p: "Connect with sponsors, partners, coaches, and fellow audience members who care.",
  },
];

const TICKETS = [
  {
    name: "General Admission",
    desc: "Reserved theater seating, full program, and post-event recap access.",
  },
  {
    name: "VIP Experience",
    desc: "Premium seating, pre-show reception, and curated meet-the-producer access.",
  },
];

const initial = { name: "", email: "", phone: "", events_interested: "" };

export default function Attend() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.events_interested) {
      toast.error("Please choose which events you're interested in.");
      return;
    }
    setSubmitting(true);
    try {
      await submitAttendeeInterest(form);
      setSubmitted(true);
      toast.success("Thank you. You're on the notification list.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo />
      <PageHero
        pageTitle="Attend The Show"
        eyebrowNumber="01"
        eyebrow="Be In The Room"
        title="Attend"
        highlight="The Show"
        tagline="You will experience emerging voices with passion, purpose, and the courage to step forward."
        description="Sign up below and we will notify you the moment tickets are released for each pilot event."
      />

      {/* WHAT TO EXPECT */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="02">What To Expect</SectionLabel>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {EXPERIENCE.map((e) => (
              <div key={e.h} className="card-dark p-8">
                <div className="flex items-center gap-3">
                  <CardMark />
                  <h3 className="font-shoulders font-bold uppercase tracking-wider text-xl text-white">
                    {e.h}
                  </h3>
                </div>
                <div className="gold-line" />
                <p className="font-manrope text-soft-gray text-sm mt-3 leading-relaxed">
                  {e.p}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILOT EVENT DATES */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="03">Pilot Event Dates</SectionLabel>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="corner-bracket card-dark p-10">
              <div className="label-bebas text-gold">Pilot One</div>
              <h3 className="font-bebas text-white text-4xl mt-2">Fall 2026</h3>
              <p className="font-cormorant text-2xl text-soft-gray mt-3">
                {BRAND.city} — venue to be announced.
              </p>
            </div>
            <div className="corner-bracket card-dark p-10">
              <div className="label-bebas text-gold">Pilot Two</div>
              <h3 className="font-bebas text-white text-4xl mt-2">Early 2027</h3>
              <p className="font-cormorant text-2xl text-soft-gray mt-3">
                {BRAND.city} — venue to be announced.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TICKET TYPES PREVIEW */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="04">Ticket Types</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] max-w-3xl">
            Pricing Announced <span className="gold-text">With Each Event.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {TICKETS.map((t) => (
              <div key={t.name} className="card-dark p-10">
                <h3 className="font-shoulders font-black uppercase tracking-wider text-2xl text-white">
                  {t.name}
                </h3>
                <div className="gold-line" />
                <p className="font-manrope text-soft-gray mt-4">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NOTIFICATION FORM */}
      <section
        id="notify"
        className="bg-navy border-t border-slate-soft"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="05" align="center">
            Ticket Notification
          </SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] text-center">
            Be First In <span className="gold-text">Line.</span>
          </h2>
          {submitted ? (
            <div
              data-testid="attend-thank-you"
              className="corner-bracket card-dark p-10 mt-8 text-center"
            >
              <div className="label-bebas text-gold">You're On The List</div>
              <h3 className="font-bebas text-white text-3xl mt-2">
                Thank You.
              </h3>
              <p className="font-manrope text-soft-gray mt-4 max-w-md mx-auto">
                We will email you when tickets are released for your selected
                events.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="attend-submit-another"
                className="btn-outline mt-8"
              >
                <Plus size={18} strokeWidth={1.8} />
                Add Another
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="attendee-interest-form"
              className="card-dark p-8 sm:p-10 mt-8 space-y-6"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-bebas" htmlFor="att-name">
                    Full Name *
                  </label>
                  <input
                    id="att-name"
                    name="name"
                    required
                    value={form.name}
                    onChange={onChange}
                    data-testid="attend-input-name"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="att-email">
                    Email *
                  </label>
                  <input
                    id="att-email"
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid="attend-input-email"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="att-phone">
                    Phone
                  </label>
                  <input
                    id="att-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="attend-input-phone"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas">Which Events? *</label>
                  <Select
                    value={form.events_interested}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, events_interested: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid="attend-select-events"
                      className="input-dark rounded-none h-[52px] text-left"
                    >
                      <SelectValue placeholder="Choose events" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-card border-slate-soft text-white">
                      <SelectItem value="Pilot One — Fall 2026">
                        Pilot One — Fall 2026
                      </SelectItem>
                      <SelectItem value="Pilot Two — Early 2027">
                        Pilot Two — Early 2027
                      </SelectItem>
                      <SelectItem value="Both Pilot Events">
                        Both Pilot Events
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="attend-submit-button"
                className="btn-gold w-full sm:w-auto"
              >
                <Bell size={18} strokeWidth={1.8} />
                {submitting ? "Sending…" : "Notify Me"}
                {!submitting && <ArrowRight size={16} />}
              </button>
              <p className="text-muted-ink text-xs font-manrope">
                We send infrequent emails — ticket releases and event-day
                logistics only.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
